<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { NEmpty } from 'naive-ui'
import AgentTranscriptMessage from './AgentTranscriptMessage.vue'
import type { ChatMessage } from '../stores/agent'

// The /agent conversation transcript. Owns its own scroll container and the
// "stick to the bottom unless the user scrolled up" behaviour, a Jump to
// latest affordance for when they have, and the live running indicator.
// Each turn renders through AgentTranscriptMessage.
const props = defineProps<{
  messages: ChatMessage[]
  agentRunning: boolean
  hasActiveThread: boolean
  liveStatus: string | null
}>()

// Within this many px of the bottom counts as "stuck" — auto-scroll only then.
const NEAR_BOTTOM_PX = 80

const transcriptEl = ref<HTMLElement | null>(null)
const scrolledUp = ref(false)
const messagesEmpty = computed(() => props.messages.length === 0)

interface MessageGroup {
  message: ChatMessage
  repeatCount: number
}

// Collapse a run of consecutive, same-role turns whose text is identical
// (trimmed) into one turn with a repeat count. A background job the agent
// polls (e.g. via the Monitor tool) can make it reply with the exact same
// short status line many times in a row, which reads as noise rather than
// as that many distinct turns (#1442). Keeps the most recent occurrence's
// metadata (timestamp/context/cost) since that's the freshest read.
const groupedMessages = computed<MessageGroup[]>(() => {
  const groups: MessageGroup[] = []
  for (const m of props.messages) {
    const last = groups[groups.length - 1]
    if (last && last.message.role === m.role && last.message.text.trim() === m.text.trim()) {
      last.message = m
      last.repeatCount += 1
    } else {
      groups.push({ message: m, repeatCount: 1 })
    }
  }
  return groups
})

function nearBottom(): boolean {
  const el = transcriptEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
}

function onScroll(): void {
  scrolledUp.value = !nearBottom()
}

function jumpToLatest(): void {
  const el = transcriptEl.value
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
}

// Auto-scroll on new output / run state, but only if the user was already at
// the bottom — don't yank them down while they're reading earlier output.
watch(
  () => [props.messages.length, props.agentRunning, props.liveStatus] as const,
  () => {
    const stick = nearBottom()
    nextTick(() => {
      if (stick && transcriptEl.value) transcriptEl.value.scrollTop = transcriptEl.value.scrollHeight
    })
  },
)
</script>

<template>
  <div
    ref="transcriptEl"
    class="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto px-4 py-5 sm:px-7"
    role="log"
    aria-live="polite"
    aria-atomic="false"
    data-testid="agent-transcript"
    @scroll.passive="onScroll"
  >
    <NEmpty
      v-if="messagesEmpty && !agentRunning"
      size="small"
      class="my-6"
      :description="hasActiveThread ? 'No output yet for this session' : 'Start a new session below'"
    />
    <AgentTranscriptMessage
      v-for="(g, i) in groupedMessages"
      :key="i"
      :message="g.message"
      :repeat-count="g.repeatCount"
    />

    <!-- Live status line: updates in place as each progress broadcast lands
         and disappears when the final turn takes its place in the list. -->
    <div v-if="agentRunning" class="flex justify-start" data-testid="agent-running">
      <div class="flex items-center gap-2.5 pt-1 text-[13px] text-ink-muted" data-testid="agent-live-bubble">
        <span class="size-2 shrink-0 animate-pulse rounded-full bg-factory" aria-hidden="true" />
        <span>Working ·</span>
        <span class="italic" data-testid="agent-live-status">{{ liveStatus ?? 'Starting…' }}</span>
      </div>
    </div>

    <button
      v-if="scrolledUp"
      type="button"
      class="sticky bottom-2 min-h-9 self-center rounded-full border border-border-control bg-surface-hover px-3.5 text-[13px] text-ink shadow-lg"
      data-testid="agent-jump-latest"
      @click="jumpToLatest"
    >
      <span aria-hidden="true">↓</span> Jump to latest
    </button>
  </div>
</template>
